import { Notifications } from '@griply/types';
import Dialog from '@mui/material/Dialog';
import React, { useMemo } from 'react';
import { GoalLifeAreaLinkField } from 'shared/components/connected/goal-life-area-link-field';
import { HabitForm } from 'shared/components/ui/habit-form';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { HabitFormFields } from 'shared/types/habit-form';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';
import { WeekDays } from 'shared/types/week-days';

import { HabitFormError } from './habit-form-error';
import { HabitFormLoading } from './habit-form-loading';

export type HabitFormDialogProps = {
  initialValues?: Partial<HabitFormFields>;
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  notifications?: Notifications;
  open?: boolean;
  onClose: () => void;
  onSubmit: (habit: HabitFormFields) => void;
  onRetry: () => void;
  isLoading?: boolean;
  isError?: boolean;
  isEdit?: boolean;
  isCustomIconAllowed?: boolean;
  isCompleted?: boolean;
  isArchived?: boolean;
  isRepeatTargetAllowed: boolean;
  isHabitCreationAllowed: boolean;
  onPremium: () => void;
  onComplete?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
};

export const HabitFormDialog: React.FC<HabitFormDialogProps> = ({
  initialValues,
  goals,
  lifeAreas,
  tags,
  dateFormat,
  weekStartsOn,
  timeFormat,
  notifications,
  open,
  onClose,
  onSubmit,
  onRetry,
  isLoading,
  isError,
  isEdit,
  isCustomIconAllowed,
  isCompleted,
  isArchived,
  isRepeatTargetAllowed,
  isHabitCreationAllowed,
  onPremium,
  onComplete,
  onArchive,
  onDelete,
}) => {
  const content = useMemo(() => {
    if (isError) {
      return (
        <HabitFormError onRetry={onRetry} onCancel={onClose} isEdit={isEdit} />
      );
    }
    if (isLoading) {
      return <HabitFormLoading />;
    }

    return (
      <HabitForm
        LinkField={GoalLifeAreaLinkField}
        initialValues={initialValues}
        goals={goals}
        lifeAreas={lifeAreas}
        tags={tags}
        dateFormat={dateFormat}
        weekStartsOn={weekStartsOn}
        timeFormat={timeFormat}
        notifications={notifications}
        onSubmit={onSubmit}
        onCancel={onClose}
        isEdit={isEdit}
        isActive={!!open}
        isCustomIconAllowed={isCustomIconAllowed}
        isCompleted={isCompleted}
        isArchived={isArchived}
        isRepeatTargetAllowed={isRepeatTargetAllowed}
        isHabitCreationAllowed={isHabitCreationAllowed}
        onPremium={onPremium}
        onComplete={onComplete}
        onArchive={onArchive}
        onDelete={onDelete}
      />
    );
  }, [
    isError,
    isLoading,
    initialValues,
    goals,
    lifeAreas,
    tags,
    dateFormat,
    weekStartsOn,
    timeFormat,
    notifications,
    onSubmit,
    onClose,
    isEdit,
    open,
    isCustomIconAllowed,
    isCompleted,
    isArchived,
    isRepeatTargetAllowed,
    isHabitCreationAllowed,
    onPremium,
    onComplete,
    onArchive,
    onDelete,
    onRetry,
  ]);

  return (
    <Dialog open={!!open} maxWidth="md">
      {content}
    </Dialog>
  );
};
